<template>
  <div v-if="schoolData">
    <div class="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6">
      <div class="max-w-7xl mx-auto">
        <!-- Header with Actions -->
        <div
          class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8"
        >
          <!-- LEFT -->
          <div>
            <h1
              class="text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight"
            >
              School Dashboard
            </h1>

            <p class="text-sm text-gray-500 mt-1">
              Manage your school information and website
            </p>
          </div>

          <!-- WEBSITE CARD -->
          <div
            v-if="userRole === 'admin'"
            class="w-full lg:w-auto bg-white border border-gray-200 rounded-3xl shadow-sm"
          >
            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 px-6 py-5 min-w-[340px]"
            >
              <!-- LEFT -->
              <div class="flex items-center gap-4">
                <!-- ICON -->
                <div
                  class="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center shadow-md shrink-0"
                >
                  <Globe class="w-7 h-7 text-white" />
                </div>

                <!-- CONTENT -->
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    School Website
                  </h3>

                  <!-- ACTIVE / DISABLED -->
                  <template v-if="schoolData?.websiteEnabled">
                    <div class="flex items-center gap-2 mt-1">
                      <span
                        class="w-2.5 h-2.5 rounded-full"
                        :class="
                          schoolData?.websiteStatus === 'disabled'
                            ? 'bg-red-500'
                            : 'bg-emerald-500 animate-pulse'
                        "
                      ></span>

                      <span
                        class="text-sm font-medium"
                        :class="
                          schoolData?.websiteStatus === 'disabled'
                            ? 'text-red-600'
                            : 'text-emerald-600'
                        "
                      >
                        {{
                          schoolData?.websiteStatus === "disabled"
                            ? "Disabled"
                            : "Website Active"
                        }}
                      </span>
                    </div>
                  </template>

                  <!-- NOT CREATED -->
                  <template v-else>
                    <p class="text-sm text-gray-500 mt-1">
                      Create your school website instantly
                    </p>
                  </template>
                </div>
              </div>

              <!-- BUTTON -->
              <div v-if="!schoolData?.websiteEnabled && userRole === 'admin'">
                <button
                  @click="createWebsite"
                  :disabled="creatingWebsite"
                  class="px-5 py-3 rounded-2xl bg-gray-900 text-white text-sm font-semibold hover:bg-black transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-60 disabled:hover:scale-100"
                >
                  <span v-if="!creatingWebsite">Create Website</span>
                  <span v-else>Creating...</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- School Header Card -->
        <div
          class="bg-white rounded-2xl border border-gray-200 shadow-sm mb-6 lg:mb-8 overflow-hidden"
        >
          <div
            class="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-5 sm:p-6 lg:p-7"
          >
            <div
              class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6"
            >
              <!-- Left Content -->
              <div class="flex items-center gap-4">
                <!-- Logo/Icon -->
                <div
                  class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg"
                >
                  <School class="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>

                <!-- School Info -->
                <div class="text-white">
                  <h2 class="text-2xl sm:text-3xl font-bold leading-tight">
                    {{ schoolData.schoolName }}
                  </h2>

                  <div
                    class="flex flex-wrap items-center gap-3 mt-2 text-blue-100 text-sm sm:text-base"
                  >
                    <div class="flex items-center gap-1">
                      <GraduationCap class="w-4 h-4" />
                      <span class="capitalize">
                        {{ schoolData.schoolType }} School
                      </span>
                    </div>

                    <div
                      v-if="schoolData.establishmentYear"
                      class="flex items-center gap-1"
                    >
                      <Calendar class="w-4 h-4" />
                      <span>Since {{ schoolData.establishmentYear }}</span>
                    </div>

                    <div class="flex items-center gap-1">
                      <MapPin class="w-4 h-4" />
                      <span>{{ schoolData.city }}, {{ schoolData.state }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Side -->
              <div
                class="flex flex-col sm:flex-row items-center gap-4 lg:gap-5"
              >
                <!-- Years Badge -->
                <div
                  class="bg-white/15 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-3 text-center text-white min-w-[100px]"
                >
                  <div class="text-2xl font-bold">
                    {{ getSchoolAge() }}
                  </div>
                  <div class="text-xs uppercase tracking-wide text-blue-100">
                    Years Legacy
                  </div>
                </div>

                <!-- Verified -->
                <div
                  class="w-14 h-14 rounded-2xl bg-green-500 shadow-lg flex items-center justify-center"
                >
                  <CheckCircle class="w-7 h-7 text-white" />
                </div>

                <!-- Edit Button -->
                <router-link
                  v-if="userRole === 'admin'"
                  to="/editSchool"
                  class="px-5 py-3 bg-white text-blue-700 font-semibold rounded-2xl hover:bg-blue-50 transition-all duration-200 shadow-md flex items-center gap-2"
                >
                  <Edit class="w-4 h-4" />
                  <span>Edit Details</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content Grid -->
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <!-- Left Column - Main Information -->
          <div class="xl:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
            <!-- Basic Information -->
            <div
              class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border p-4 sm:p-6 lg:p-8"
            >
              <div class="flex items-center space-x-3 mb-4 sm:mb-6">
                <div
                  class="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl sm:rounded-2xl flex items-center justify-center"
                >
                  <Info class="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <h3
                  class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900"
                >
                  Basic Information
                </h3>
              </div>

              <div
                class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6"
              >
                <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div
                    class="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3"
                  >
                    <School class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    <span
                      class="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide"
                    >
                      School Name
                    </span>
                  </div>
                  <p
                    class="text-sm sm:text-base lg:text-lg font-bold text-gray-900 break-words"
                  >
                    {{ schoolData.schoolName }}
                  </p>
                </div>

                <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div
                    class="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3"
                  >
                    <GraduationCap
                      class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600"
                    />
                    <span
                      class="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide"
                    >
                      School Type
                    </span>
                  </div>
                  <p
                    class="text-sm sm:text-base lg:text-lg font-bold text-gray-900 capitalize"
                  >
                    {{ schoolData.schoolType }} School
                  </p>
                </div>

                <div
                  v-if="schoolData.establishmentYear"
                  class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6"
                >
                  <div
                    class="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3"
                  >
                    <Calendar class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    <span
                      class="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide"
                    >
                      Established
                    </span>
                  </div>
                  <p
                    class="text-sm sm:text-base lg:text-lg font-bold text-gray-900"
                  >
                    {{ schoolData.establishmentYear }}
                  </p>
                </div>

                <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div
                    class="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3"
                  >
                    <Clock class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    <span
                      class="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide"
                    >
                      Age
                    </span>
                  </div>
                  <p
                    class="text-sm sm:text-base lg:text-lg font-bold text-gray-900"
                  >
                    {{ getSchoolAge() }} Years
                  </p>
                </div>
              </div>
            </div>
            <!-- ============================= -->
            <!-- LEAVE OVERVIEW SECTION -->
            <!-- ============================= -->
            <div
              class="bg-white rounded-3xl border border-gray-200 p-6 sm:p-8 shadow-sm"
            >
              <!-- Header -->
              <div class="flex items-center justify-between mb-8">
                <div class="flex items-start gap-3">
                  <div
                    class="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center"
                  >
                    <CalendarDays class="w-5 h-5 text-indigo-600" />
                  </div>

                  <div>
                    <h3 class="text-xl sm:text-2xl font-bold text-gray-900">
                      {{
                        userRole === "admin"
                          ? "Overall School Leave Balance"
                          : "My Leave Balance"
                      }}
                    </h3>

                    <p class="text-sm text-gray-500">
                      {{
                        userRole === "admin"
                          ? "View and manage total leave allocations across the entire school."
                          : "Track your personal leave allocations."
                      }}
                    </p>
                  </div>
                </div>

                <!-- Admin Create Button -->
                <button
                  v-if="
                    userRole === 'admin' &&
                    (!leaveBalance || !leaveBalance.balances?.length)
                  "
                  @click="openPolicyModal"
                  class="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm font-semibold"
                >
                  + Create Policy
                </button>
              </div>

              <!-- IF BALANCES EXIST -->
              <div
                v-if="leaveBalance && leaveBalance.balances?.length"
                class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
              >
                <div
                  v-for="(item, index) in leaveBalance.balances"
                  :key="index"
                  class="relative rounded-3xl p-6 bg-gradient-to-br from-white to-indigo-50/40 border border-indigo-100 group"
                >
                  <!-- Accent Glow -->
                  <div
                    class="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-indigo-100/40 to-blue-100/40 transition duration-500"
                  ></div>

                  <!-- Top Row -->
                  <div class="relative flex justify-between items-start mb-6">
                    <div>
                      <p class="text-sm text-gray-500 mb-1">Leave Type</p>
                      <h4 class="text-xl font-bold text-gray-900 turncate">
                        {{ item.type }}
                      </h4>
                    </div>

                    <div class="flex items-center gap-3">
                      <!-- Remaining Big Badge -->
                      <div
                        class="flex flex-col items-center justify-center bg-white rounded-2xl px-4 py-2 shadow-sm border"
                      >
                        <span
                          class="text-2xl font-bold"
                          :class="
                            item.remaining === 0
                              ? 'text-red-600'
                              : 'text-indigo-600'
                          "
                        >
                          {{ item.remaining }}
                        </span>
                        <span class="text-xs text-gray-500">
                          {{ userRole === "admin" ? "Days" : "Left" }}
                        </span>
                      </div>

                      <!-- 3 Dot Menu -->
                      <div
                        v-if="userRole === 'admin'"
                        class="relative"
                      >
                        <button
                          @click.stop="toggleMenu(index)"
                          class="p-2 rounded-full hover:bg-gray-100 transition"
                        >
                          <MoreVertical class="w-5 h-5 text-gray-600" />
                        </button>

                        <!-- Dropdown -->
                        <div
                          v-if="activeMenu === index"
                          class="absolute right-0 mt-2 w-36 bg-white rounded-xl shadow-lg border z-50"
                        >
                          <button
                            @click="
                              openEditModal(item);
                              closeMenu();
                            "
                            class="w-full flex items-center gap-2 text-left px-4 py-2 text-sm hover:bg-indigo-50 text-indigo-600"
                          >
                            <Edit3 class="w-4 h-4" />
                            <span>Update</span>
                          </button>

                          <button
                            @click="
                              requestDeleteLeaveType(item);
                              closeMenu();
                            "
                            class="w-full flex items-center gap-2 text-left px-4 py-2 text-sm hover:bg-red-50 text-red-600"
                          >
                            <Trash2 class="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Usage Stats -->
                  <!-- Usage Stats (Hidden for Admin) -->
                  <div
                    v-if="userRole !== 'admin'"
                    class="relative mb-4"
                  >
                    <div
                      class="flex justify-between text-sm text-gray-600 mb-2"
                    >
                      <span>Used: {{ item.used }}</span>
                      <span>Total: {{ item.total }}</span>
                    </div>

                    <!-- Premium Progress Bar -->
                    <div
                      class="w-full h-3 bg-gray-200 rounded-full overflow-hidden"
                    >
                      <div
                        class="h-3 rounded-full transition-all duration-700"
                        :class="
                          item.remaining === 0
                            ? 'bg-red-500'
                            : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500'
                        "
                        :style="{
                          width:
                            item.total > 0
                              ? (item.used / item.total) * 100 + '%'
                              : '0%',
                        }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- IF NO POLICY -->
              <div
                v-else
                class="text-center py-12"
              >
                <div
                  class="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4"
                >
                  <Calendar class="w-8 h-8 text-indigo-600" />
                </div>

                <h4 class="text-lg font-semibold text-gray-800 mb-2">
                  {{
                    userRole === "admin"
                      ? "No Leave Policy Configured"
                      : "Leave Policy Not Available Yet"
                  }}
                </h4>

                <p class="text-sm text-gray-500 mb-6">
                  {{
                    userRole === "admin"
                      ? "Create a leave policy to start tracking leave balances."
                      : "Please wait until the admin creates a leave policy to enable leave tracking."
                  }}
                </p>

                <button
                  v-if="userRole === 'admin'"
                  @click="openPolicyModal"
                  class="px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-semibold text-sm"
                >
                  Create Leave Policy
                </button>
              </div>
            </div>
            <!-- ============================= -->
            <!-- TEACHER ATTENDANCE TIMING -->
            <!-- ============================= -->
            <div
              v-if="userRole === 'admin'"
              class="bg-white rounded-3xl border border-gray-200 p-6 sm:p-8 shadow-sm"
            >
              <!-- Header -->
              <div class="flex items-center justify-between mb-6">
                <div class="flex items-start gap-3">
                  <div
                    class="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"
                  >
                    <Clock class="w-5 h-5 text-blue-600" />
                  </div>

                  <div>
                    <h3 class="text-xl sm:text-2xl font-bold text-gray-900">
                      Teacher Attendance Timing
                    </h3>
                    <p class="text-sm text-gray-500">
                      Configure default check-in and check-out time for
                      teachers.
                    </p>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <!-- Check In -->
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-2">
                    Check-In Time
                  </label>

                  <input
                    v-model="attendanceTiming.checkInTime"
                    type="time"
                    class="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>

                <!-- Check Out -->
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-2">
                    Check-Out Time
                  </label>

                  <input
                    v-model="attendanceTiming.checkOutTime"
                    type="time"
                    class="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <!-- Save Button -->
              <div class="mt-6 flex justify-end">
                <button
                  @click="updateAttendanceTiming"
                  :disabled="isSavingTiming"
                  class="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-60"
                >
                  <svg
                    v-if="isSavingTiming"
                    class="animate-spin h-5 w-5 text-white"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                      fill="none"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>

                  <span>
                    {{ isSavingTiming ? "Saving..." : "Update Timing" }}
                  </span>
                </button>
              </div>
            </div>
            <!-- Address Information -->
            <div
              class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border p-4 sm:p-6 lg:p-8"
            >
              <div class="flex items-center space-x-3 mb-4 sm:mb-6">
                <div
                  class="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-xl sm:rounded-2xl flex items-center justify-center"
                >
                  <MapPin class="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <h3
                  class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900"
                >
                  Address Information
                </h3>
              </div>

              <div class="space-y-4 sm:space-y-6">
                <!-- Full Address Display -->
                <div
                  class="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-green-200"
                >
                  <div class="flex items-start space-x-3">
                    <MapPin
                      class="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mt-1 flex-shrink-0"
                    />
                    <div class="min-w-0 flex-1">
                      <h4
                        class="font-semibold text-gray-900 mb-2 text-sm sm:text-base"
                      >
                        Complete Address
                      </h4>
                      <div
                        class="text-gray-700 leading-relaxed text-sm sm:text-base space-y-1"
                      >
                        <p class="break-words">{{ schoolData.address }}</p>
                        <p
                          v-if="schoolData.village"
                          class="break-words"
                        >
                          {{ schoolData.village }}
                        </p>
                        <p
                          v-if="schoolData.postOffice"
                          class="break-words"
                        >
                          Post Office: {{ schoolData.postOffice }}
                        </p>
                        <p class="break-words">
                          {{ schoolData.city }}, {{ schoolData.state }}
                          {{ schoolData.pincode }}
                        </p>
                        <p class="font-medium break-words">
                          {{ getCountryName(schoolData.country) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Address Components Grid -->
                <div
                  class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
                >
                  <div class="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                    <div class="flex items-center space-x-2 mb-2">
                      <Building class="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                      <span
                        class="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                      >
                        City
                      </span>
                    </div>
                    <p
                      class="font-bold text-gray-900 text-sm sm:text-base break-words"
                    >
                      {{ schoolData.city }}
                    </p>
                  </div>

                  <div class="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                    <div class="flex items-center space-x-2 mb-2">
                      <Map class="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                      <span
                        class="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                      >
                        State
                      </span>
                    </div>
                    <p
                      class="font-bold text-gray-900 text-sm sm:text-base break-words"
                    >
                      {{ schoolData.state }}
                    </p>
                  </div>

                  <div class="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                    <div class="flex items-center space-x-2 mb-2">
                      <Hash class="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                      <span
                        class="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                      >
                        PIN Code
                      </span>
                    </div>
                    <p class="font-bold text-gray-900 text-sm sm:text-base">
                      {{ schoolData.pincode }}
                    </p>
                  </div>

                  <div
                    v-if="schoolData.village"
                    class="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4"
                  >
                    <div class="flex items-center space-x-2 mb-2">
                      <TreePine class="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                      <span
                        class="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                      >
                        Village
                      </span>
                    </div>
                    <p
                      class="font-bold text-gray-900 text-sm sm:text-base break-words"
                    >
                      {{ schoolData.village }}
                    </p>
                  </div>

                  <div
                    v-if="schoolData.postOffice"
                    class="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4"
                  >
                    <div class="flex items-center space-x-2 mb-2">
                      <Mail class="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                      <span
                        class="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                      >
                        Post Office
                      </span>
                    </div>
                    <p
                      class="font-bold text-gray-900 text-sm sm:text-base break-words"
                    >
                      {{ schoolData.postOffice }}
                    </p>
                  </div>

                  <div class="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                    <div class="flex items-center space-x-2 mb-2">
                      <Globe class="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                      <span
                        class="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                      >
                        Country
                      </span>
                    </div>
                    <p
                      class="font-bold text-gray-900 text-sm sm:text-base break-words"
                    >
                      {{ getCountryName(schoolData.country) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description Section -->
            <div
              v-if="schoolData.description"
              class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border p-4 sm:p-6 lg:p-8"
            >
              <div class="flex items-center space-x-3 mb-4 sm:mb-6">
                <div
                  class="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-xl sm:rounded-2xl flex items-center justify-center"
                >
                  <FileText class="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <h3
                  class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900"
                >
                  About the School
                </h3>
              </div>
              <div
                class="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-orange-200"
              >
                <p class="text-gray-700 leading-relaxed text-sm sm:text-base">
                  {{ schoolData.description }}
                </p>
              </div>
            </div>
          </div>

          <!-- Right Column - Contact & Quick Info -->
          <div class="space-y-4 sm:space-y-6 lg:space-y-8">
            <!-- Contact Information -->
            <div
              class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border p-4 sm:p-6 lg:p-8"
            >
              <div class="flex items-center space-x-3 mb-4 sm:mb-6">
                <div
                  class="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl sm:rounded-2xl flex items-center justify-center"
                >
                  <Phone class="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <h3 class="text-lg sm:text-xl font-bold text-gray-900">
                  Contact Information
                </h3>
              </div>

              <div class="space-y-3 sm:space-y-4">
                <!-- Primary Phone -->
                <div class="bg-purple-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 sm:w-10 sm:h-10 bg-purple-500 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                    >
                      <Phone class="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p
                        class="text-xs sm:text-sm font-semibold text-purple-600 uppercase tracking-wide"
                      >
                        Primary Phone
                      </p>
                      <p
                        class="text-sm sm:text-base lg:text-lg font-bold text-gray-900 break-all"
                      >
                        {{ schoolData.primaryPhone }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Secondary Phone -->
                <div
                  v-if="schoolData.secondaryPhone"
                  class="bg-blue-50 rounded-xl sm:rounded-2xl p-3 sm:p-4"
                >
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                    >
                      <PhoneCall class="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p
                        class="text-xs sm:text-sm font-semibold text-blue-600 uppercase tracking-wide"
                      >
                        Secondary Phone
                      </p>
                      <p
                        class="text-sm sm:text-base lg:text-lg font-bold text-gray-900 break-all"
                      >
                        {{ schoolData.secondaryPhone }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Email -->
                <div class="bg-green-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 sm:w-10 sm:h-10 bg-green-500 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                    >
                      <Mail class="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p
                        class="text-xs sm:text-sm font-semibold text-green-600 uppercase tracking-wide"
                      >
                        Email Address
                      </p>
                      <p
                        class="text-sm sm:text-base lg:text-lg font-bold text-gray-900 break-all"
                      >
                        {{ schoolData.email }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Website -->
                <div
                  v-if="schoolData.website"
                  class="bg-indigo-50 rounded-xl sm:rounded-2xl p-3 sm:p-4"
                >
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                    >
                      <Globe class="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p
                        class="text-xs sm:text-sm font-semibold text-indigo-600 uppercase tracking-wide"
                      >
                        Website
                      </p>
                      <a
                        :href="schoolData.website"
                        target="_blank"
                        class="text-sm sm:text-base lg:text-lg font-bold text-indigo-600 hover:text-indigo-800 break-all block"
                      >
                        {{ schoolData.website }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div
              class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border p-4 sm:p-6 lg:p-8"
            >
              <h3
                class="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6"
              >
                Quick Actions
              </h3>
              <div class="space-y-2 sm:space-y-3">
                <RouterLink
                  to="/teacher"
                  class="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl transition-colors duration-200 flex items-center space-x-3 text-sm sm:text-base"
                >
                  <Users class="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>View Teachers</span>
                </RouterLink>
                <RouterLink
                  to="/students"
                  class="w-full bg-green-50 hover:bg-green-100 text-green-700 font-semibold py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl transition-colors duration-200 flex items-center space-x-3 text-sm sm:text-base"
                >
                  <UserCheck class="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>View Students</span>
                </RouterLink>
                <RouterLink
                  to="/reports"
                  class="w-full bg-purple-50 hover:bg-purple-100 text-purple-700 font-semibold py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl transition-colors duration-200 flex items-center space-x-3 text-sm sm:text-base"
                >
                  <BarChart3 class="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>View Reports</span>
                </RouterLink>
                <RouterLink
                  to="/setting"
                  class="w-full bg-orange-50 hover:bg-orange-100 text-orange-700 font-semibold py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl transition-colors duration-200 flex items-center space-x-3 text-sm sm:text-base"
                >
                  <Settings class="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>School Settings</span>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div
    v-else-if="isLoading"
    class="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6"
  >
    <div class="max-w-7xl mx-auto">
      <!-- Loading Header -->
      <div
        class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 mb-6 lg:mb-8 overflow-hidden"
      >
        <div
          class="bg-gradient-to-r from-blue-600 to-indigo-700 p-4 sm:p-6 lg:p-8"
        >
          <div
            class="flex flex-col lg:flex-row lg:items-center lg:space-x-8 space-y-4 lg:space-y-0"
          >
            <div
              class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-white/20 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto lg:mx-0 animate-pulse"
            >
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white/30 rounded-xl"
              ></div>
            </div>
            <div class="flex-1 text-center lg:text-left">
              <div
                class="h-6 sm:h-8 bg-white/20 rounded-lg mb-2 sm:mb-4 animate-pulse"
              ></div>
              <div class="space-y-2">
                <div
                  class="h-3 sm:h-4 bg-white/20 rounded w-3/4 mx-auto lg:mx-0 animate-pulse"
                ></div>
                <div
                  class="h-3 sm:h-4 bg-white/20 rounded w-1/2 mx-auto lg:mx-0 animate-pulse"
                ></div>
              </div>
            </div>
            <div class="flex items-center justify-center space-x-4">
              <div class="text-center">
                <div
                  class="w-8 h-6 sm:w-12 sm:h-8 bg-white/20 rounded animate-pulse mb-2"
                ></div>
                <div
                  class="w-12 h-3 sm:w-16 sm:h-4 bg-white/20 rounded animate-pulse"
                ></div>
              </div>
              <div
                class="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/20 rounded-xl sm:rounded-2xl animate-pulse"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Content Grid -->
      <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        <!-- Left Column Loading -->
        <div class="xl:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
          <div
            class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8"
          >
            <div class="flex items-center space-x-3 mb-4 sm:mb-6">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-xl sm:rounded-2xl animate-pulse"
              ></div>
              <div
                class="h-5 sm:h-6 bg-gray-200 rounded w-32 sm:w-48 animate-pulse"
              ></div>
            </div>
            <div
              class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6"
            >
              <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div
                  class="h-3 sm:h-4 bg-gray-200 rounded mb-2 sm:mb-3 animate-pulse"
                ></div>
                <div class="h-4 sm:h-6 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div
                  class="h-3 sm:h-4 bg-gray-200 rounded mb-2 sm:mb-3 animate-pulse"
                ></div>
                <div class="h-4 sm:h-6 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div
                  class="h-3 sm:h-4 bg-gray-200 rounded mb-2 sm:mb-3 animate-pulse"
                ></div>
                <div class="h-4 sm:h-6 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div
                  class="h-3 sm:h-4 bg-gray-200 rounded mb-2 sm:mb-3 animate-pulse"
                ></div>
                <div class="h-4 sm:h-6 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          <div
            class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8"
          >
            <div class="flex items-center space-x-3 mb-4 sm:mb-6">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-xl sm:rounded-2xl animate-pulse"
              ></div>
              <div
                class="h-5 sm:h-6 bg-gray-200 rounded w-32 sm:w-48 animate-pulse"
              ></div>
            </div>
            <div class="space-y-4 sm:space-y-6">
              <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div
                  class="h-3 sm:h-4 bg-gray-200 rounded mb-2 sm:mb-3 animate-pulse"
                ></div>
                <div class="space-y-2">
                  <div
                    class="h-3 sm:h-4 bg-gray-200 rounded animate-pulse"
                  ></div>
                  <div
                    class="h-3 sm:h-4 bg-gray-200 rounded w-3/4 animate-pulse"
                  ></div>
                  <div
                    class="h-3 sm:h-4 bg-gray-200 rounded w-1/2 animate-pulse"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column Loading -->
        <div class="space-y-4 sm:space-y-6 lg:space-y-8">
          <div
            class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8"
          >
            <div class="flex items-center space-x-3 mb-4 sm:mb-6">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-xl sm:rounded-2xl animate-pulse"
              ></div>
              <div
                class="h-5 sm:h-6 bg-gray-200 rounded w-24 sm:w-32 animate-pulse"
              ></div>
            </div>
            <div class="space-y-3 sm:space-y-4">
              <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div class="flex items-center space-x-3">
                  <div
                    class="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-lg sm:rounded-xl animate-pulse"
                  ></div>
                  <div class="flex-1">
                    <div
                      class="h-2 sm:h-3 bg-gray-200 rounded mb-2 animate-pulse"
                    ></div>
                    <div
                      class="h-4 sm:h-5 bg-gray-200 rounded animate-pulse"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div class="flex items-center space-x-3">
                  <div
                    class="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-lg sm:rounded-xl animate-pulse"
                  ></div>
                  <div class="flex-1">
                    <div
                      class="h-2 sm:h-3 bg-gray-200 rounded mb-2 animate-pulse"
                    ></div>
                    <div
                      class="h-4 sm:h-5 bg-gray-200 rounded animate-pulse"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div class="flex items-center justify-center mt-6 lg:mt-8">
        <div
          class="bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 px-4 sm:px-6 py-3 sm:py-4"
        >
          <div class="flex items-center space-x-3">
            <div
              class="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"
            ></div>
            <span class="text-gray-700 font-medium text-sm sm:text-base"
              >Loading school details...</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- No Results State -->
  <div
    v-else
    class="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6 flex items-center justify-center"
  >
    <div class="max-w-sm sm:max-w-md mx-auto text-center">
      <div
        class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-xl border p-6 sm:p-8 lg:p-12"
      >
        <div
          class="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6"
        >
          <School class="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
        </div>
        <h3 class="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
          No School Found
        </h3>
        <p
          class="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base"
        >
          We couldn't find any school details to display. This might be because
          no school has been set up yet or there was an issue loading the data.
        </p>
        <div class="space-y-2 sm:space-y-3">
          <button
            @click="reloadPage"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2 text-sm sm:text-base"
          >
            <svg
              class="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>Try Again</span>
          </button>
          <router-link
            to="/createSchool"
            class="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2 text-sm sm:text-base"
          >
            <svg
              class="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            <span>Create New School</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Policy Form Modal -->
  <transition name="modal">
    <div
      v-if="showPolicyForm"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-2xl max-w-2xl w-full p-6 sm:p-8">
        <h2 class="text-2xl font-bold text-gray-900 mb-6">
          Create Leave Policy
        </h2>

        <!-- Leave Types Form -->
        <div class="space-y-4 mb-6">
          <div
            v-for="(leaveType, index) in policyForm.leaveTypes"
            :key="index"
            class="flex gap-3 items-end"
          >
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Leave Type
              </label>
              <input
                v-model="leaveType.type"
                type="text"
                placeholder="e.g., Casual, Sick"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Yearly Limit
              </label>
              <input
                v-model.number="leaveType.yearlyLimit"
                type="number"
                placeholder="15"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              @click="removeLeaveType(index)"
              class="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm font-medium"
            >
              Remove
            </button>
          </div>
        </div>

        <!-- Add More Button -->
        <button
          @click="addLeaveType"
          class="mb-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
        >
          + Add Leave Type
        </button>

        <!-- Form Actions -->
        <div class="flex gap-3 justify-end">
          <button
            @click="showPolicyForm = false"
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Cancel
          </button>
          <button
            @click="submitPolicyForm"
            class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          >
            Create Policy
          </button>
        </div>
      </div>
    </div>
  </transition>
  <!-- Edit Total Modal -->

  <div
    v-if="showEditModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
  >
    <div
      class="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-8 relative"
    >
      <!-- Close Button -->
      <button
        @click="showEditModal = false"
        class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
      >
        ✕
      </button>

      <!-- Title -->
      <h2
        class="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2"
      >
        <span class="p-2 rounded-lg bg-blue-100">
          <Pencil class="w-5 h-5 text-blue-600" />
        </span>
        Update Leave
      </h2>

      <p class="text-sm text-gray-500 mb-6">
        {{ editingBalance.type }} Leave Balance
      </p>

      <!-- Input Section -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-600 mb-2">
          New Total Days
        </label>

        <input
          v-model.number="editingBalance.newTotal"
          type="number"
          class="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg font-semibold transition"
        />

        <div class="mt-3 text-xs text-gray-500 flex justify-between">
          <span>Current: {{ editingBalance.total }}</span>
          <!-- <span>Used: {{ editingBalance.used }}</span> -->
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button
          @click="showEditModal = false"
          class="flex-1 py-3 rounded-2xl bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition"
        >
          Cancel
        </button>

        <button
          @click="submitEditBalance"
          :disabled="isUpdating"
          class="flex-1 py-3 rounded-2xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <!-- Loader -->
          <svg
            v-if="isUpdating"
            class="animate-spin h-5 w-5 text-white"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
              fill="none"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>

          <span>
            {{ isUpdating ? "Updating..." : "Update" }}
          </span>
        </button>
      </div>
    </div>
  </div>

  <ConfirmModal
    :show="showConfirmModal"
    :title="confirmConfig.title"
    :description="confirmConfig.description"
    :buttonText="confirmConfig.buttonText"
    :isDestructive="confirmConfig.isDestructive"
    :isLoading="confirmLoading"
    @cancel="showConfirmModal = false"
    @confirm="confirmConfig.action"
  />
  <!-- ======================================== -->
  <!-- CREATE WEBSITE CONFIRMATION MODAL -->
  <!-- ======================================== -->

  <transition name="modal">
    <div
      v-if="showWebsiteModal"
      class="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        class="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <!-- Top Gradient -->
        <div
          class="h-2 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500"
        ></div>

        <div class="p-6 sm:p-8">
          <!-- Icon -->
          <div
            class="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-indigo-600 to-cyan-500 flex items-center justify-center shadow-xl mb-6"
          >
            <Globe class="w-10 h-10 text-white" />
          </div>

          <!-- Title -->
          <div class="text-center">
            <h2 class="text-2xl sm:text-3xl font-bold text-gray-900">
              Launch Your Website
            </h2>

            <p class="mt-3 text-gray-500 leading-relaxed">
              Your school website will be automatically generated and connected
              with Educanium.
            </p>
          </div>

          <!-- Features -->
          <div class="mt-8 space-y-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center"
              >
                <CheckCircle class="w-5 h-5 text-green-600" />
              </div>

              <div>
                <p class="font-semibold text-gray-900">Automatic Data Sync</p>

                <p class="text-sm text-gray-500">
                  Teachers, notices, events & students sync automatically.
                </p>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"
              >
                <Globe class="w-5 h-5 text-blue-600" />
              </div>

              <div>
                <p class="font-semibold text-gray-900">
                  Free Subdomain Included
                </p>

                <p class="text-sm text-gray-500">
                  Example: vinoda-academy.educanium.com
                </p>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center"
              >
                <Sparkles class="w-5 h-5 text-purple-600" />
              </div>

              <div>
                <p class="font-semibold text-gray-900">
                  Premium School Template
                </p>

                <p class="text-sm text-gray-500">
                  Modern responsive design optimized for schools.
                </p>
              </div>
            </div>
          </div>

          <!-- Domain Preview -->
          <div
            class="mt-8 rounded-2xl bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 p-5"
          >
            <p
              class="text-xs uppercase tracking-widest text-indigo-500 font-semibold mb-2"
            >
              Your Website URL
            </p>

            <div class="text-lg sm:text-xl font-bold text-indigo-700 break-all">
              {{
                schoolData?.schoolName?.toLowerCase()?.replace(/\s+/g, "-")
              }}.educanium.com
            </div>
          </div>

          <!-- Actions -->
          <div class="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              @click="showWebsiteModal = false"
              class="flex-1 py-4 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold transition"
            >
              Cancel
            </button>

            <button
              @click="confirmCreateWebsite"
              :disabled="creatingWebsite"
              class="flex-1 py-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold shadow-lg hover:scale-[1.02] transition-all duration-300 disabled:opacity-60"
            >
              <span v-if="!creatingWebsite"> Create Website </span>

              <span
                v-else
                class="flex items-center justify-center gap-2"
              >
                <svg
                  class="animate-spin h-5 w-5 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                    fill="none"
                  />

                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>

                Creating...
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
  <!-- ======================================== -->
  <!-- DISABLE WEBSITE CONFIRMATION MODAL -->
  <!-- ======================================== -->

  <transition name="modal">
    <div
      v-if="showDisableWebsiteModal"
      class="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        class="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <!-- Top Border -->
        <div class="h-2 bg-gradient-to-r from-red-500 to-red-700"></div>

        <div class="p-6 sm:p-8">
          <!-- Icon -->
          <div
            class="w-20 h-20 mx-auto rounded-3xl bg-red-100 flex items-center justify-center mb-6"
          >
            <Globe class="w-10 h-10 text-red-600" />
          </div>

          <!-- Title -->
          <div class="text-center">
            <h2 class="text-2xl font-bold text-gray-900">Disable Website?</h2>

            <p class="mt-3 text-gray-500 leading-relaxed">
              Your school website will become inaccessible to visitors until you
              enable it again.
            </p>
          </div>

          <!-- Warning Box -->
          <div class="mt-6 rounded-2xl bg-red-50 border border-red-100 p-4">
            <p class="text-sm text-red-700 font-medium">Website URL:</p>

            <p class="mt-1 text-sm break-all text-red-600">
              {{
                schoolData.customDomain || `${schoolData.slug}.educanium.com`
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
  import { ref, onMounted, computed, reactive } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  import ConfirmModal from "@/components/Students/ConfirmationPopupModal.vue";
  import {
    Edit,
    School,
    GraduationCap,
    Calendar,
    MapPin,
    CheckCircle,
    Info,
    Building,
    Map,
    Hash,
    TreePine,
    Mail,
    Globe,
    Phone,
    PhoneCall,
    FileText,
    Users,
    UserCheck,
    BarChart3,
    Settings,
    Clock,
    MoreVertical,
    Edit3,
    Trash2,
    Pencil,
    CalendarDays,
  } from "lucide-vue-next";
  import store from "@/store";
  const { showToast } = useGlobalToast();
  // 🎯 UI state
  const showMenu = ref(false);
  const isLoading = ref(true);
  const showPolicyForm = ref(false);
  const showEditModal = ref(false);
  const isUpdating = ref(false);
  const showConfirmModal = ref(false);
  const showWebsiteModal = ref(false);
  const creatingWebsite = ref(false);
  const confirmLoading = ref(false);
  const showDisableWebsiteModal = ref(false);
  const attendanceTiming = reactive({
    checkInTime: "09:00",
    checkOutTime: "14:30",
  });

  const isSavingTiming = ref(false);
  const route = useRoute();
  const router = useRouter();
  const confirmConfig = reactive({
    title: "",
    description: "",
    buttonText: "Confirm",
    isDestructive: false,
    action: null,
  });

  const activeMenu = ref(null);

  const toggleMenu = (index) => {
    activeMenu.value = activeMenu.value === index ? null : index;
  };

  const closeMenu = () => {
    activeMenu.value = null;
  };

  // Policy form state
  // ✅ Default Leave Types (Single Source of Truth)
  const DEFAULT_LEAVE_TYPES = [
    { type: "Casual", yearlyLimit: 12 },
    { type: "Sick", yearlyLimit: 10 },
    { type: "Personal", yearlyLimit: 5 },
    { type: "Maternity", yearlyLimit: 90 },
    { type: "Paternity", yearlyLimit: 15 },
    { type: "Unpaid", yearlyLimit: 0 },
  ];

  // ✅ Policy form state
  const policyForm = reactive({
    leaveTypes: [...DEFAULT_LEAVE_TYPES],
  });
  // Open policy modal with fresh defaults
  const openPolicyModal = () => {
    policyForm.leaveTypes = [...DEFAULT_LEAVE_TYPES];
    showPolicyForm.value = true;
  };
  // Edit balance state
  const editingBalance = reactive({
    type: "",
    total: 0,
    used: 0,
    remaining: 0,
    newTotal: 0,
  });

  // Open edit modal for updating total
  const openEditModal = (item) => {
    editingBalance.type = item.type;
    editingBalance.total = item.total;
    editingBalance.used = item.used;
    editingBalance.remaining = item.remaining;
    editingBalance.newTotal = item.total;
    showEditModal.value = true;
  };

  // Submit updated total
  const submitEditBalance = async () => {
    if (editingBalance.newTotal < editingBalance.used) {
      showToast("error", "New total cannot be less than used leaves!");
      return;
    }

    try {
      isUpdating.value = true;
      await store.dispatch("updateLeaveBalance", {
        leaveType: editingBalance.type,
        total: editingBalance.newTotal,
      });

      await store.dispatch("fetchMyLeaveBalance");
      showEditModal.value = false;
      showToast("success", "Leave balance updated successfully!");
    } catch (err) {
      console.error("Failed to update balance", err);
      showToast("error", "Failed to update leave balance");
    } finally {
      isUpdating.value = false;
    }
  };
  const updateAttendanceTiming = async () => {
    try {
      isSavingTiming.value = true;

      const today = new Date().toISOString().split("T")[0];

      await store.dispatch("updateTeacherAttendanceTiming", {
        checkInTime: `${today}T${attendanceTiming.checkInTime}:00`,
        checkOutTime: `${today}T${attendanceTiming.checkOutTime}:00`,
      });

      showToast("success", "Teacher attendance timing updated!");
    } catch (err) {
      showToast("error", "Failed to update attendance timing");
    } finally {
      isSavingTiming.value = false;
    }
  };

  const requestDeleteLeaveType = (item) => {
    if (item.used > 0) {
      showToast(
        "error",
        `Cannot delete ${item.type} because ${item.used} leave(s) have already been used.`,
      );
      return;
    }

    confirmConfig.title = "Delete Leave Type";
    confirmConfig.description = `Are you sure you want to permanently delete "${item.type}"? This action cannot be undone.`;
    confirmConfig.buttonText = "Delete";
    confirmConfig.isDestructive = true;

    confirmConfig.action = async () => {
      confirmLoading.value = true;

      try {
        await store.dispatch("deleteLeaveType", item.type);
        await store.dispatch("fetchMyLeaveBalance");

        showToast("success", "Leave type deleted successfully!");
        showConfirmModal.value = false;
      } catch (err) {
        showToast(
          "error",
          err.response?.data?.error || "Failed to delete leave type.",
        );
      } finally {
        confirmLoading.value = false;
      }
    };

    showConfirmModal.value = true;
  };

  // Add new leave type row
  const addLeaveType = () => {
    policyForm.leaveTypes.push({ type: "", yearlyLimit: 0 });
  };

  // Remove leave type row
  const removeLeaveType = (index) => {
    policyForm.leaveTypes.splice(index, 1);
  };

  // Submit policy form
  const submitPolicyForm = async () => {
    // Validate form
    if (policyForm.leaveTypes.length === 0) {
      showToast("error", "Please add at least one leave type");
      return;
    }

    const isValid = policyForm.leaveTypes.every(
      (lt) =>
        lt.type &&
        lt.type.trim().length > 0 &&
        lt.yearlyLimit !== null &&
        lt.yearlyLimit !== undefined &&
        lt.yearlyLimit >= 0,
    );

    if (!isValid) {
      showToast("error", "Please fill in all fields correctly");
      return;
    }

    // 🔥 OPEN CONFIRM MODAL INSTEAD OF DIRECT SUBMIT
    confirmConfig.title = "Create Leave Policy";
    confirmConfig.description =
      "Are you sure you want to create/update this leave policy? This will apply to all users in the school.";
    confirmConfig.buttonText = "Create Policy";
    confirmConfig.isDestructive = false;

    confirmConfig.action = async () => {
      confirmLoading.value = true;

      try {
        await store.dispatch("createOrUpdateLeavePolicy", {
          leaveTypes: policyForm.leaveTypes,
        });

        await store.dispatch("fetchMyLeaveBalance");

        showPolicyForm.value = false;
        policyForm.leaveTypes = [...DEFAULT_LEAVE_TYPES];

        showToast("success", "Leave policy created successfully!");
        showConfirmModal.value = false;
      } catch (err) {
        showToast(
          "error",
          err.response?.data?.error || "Failed to create leave policy",
        );
      } finally {
        confirmLoading.value = false;
      }
    };

    showConfirmModal.value = true;
  };

  // 🌍 Country lookup
  const countries = ref([
    { code: "US", name: "United States" },
    { code: "IN", name: "India" },
    { code: "UK", name: "United Kingdom" },
    { code: "CA", name: "Canada" },
    { code: "AU", name: "Australia" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "JP", name: "Japan" },
    { code: "CN", name: "China" },
    { code: "BR", name: "Brazil" },
  ]);

  // 🔁 Fetch school details from Vuex on mount
  onMounted(async () => {
    try {
      isLoading.value = true;

      // ✅ Fetch school data
      await store.dispatch("fetchMySchool");

      // ✅ Fetch leave balance
      await store.dispatch("fetchMyLeaveBalance");

      // ✅ Fetch attendance timing safely
      try {
        const timing = await store.dispatch("fetchTeacherAttendanceTiming");

        console.log("TIMING RESPONSE:", timing);

        if (timing?.checkInTime && timing?.checkOutTime) {
          const checkIn = new Date(timing.checkInTime);
          const checkOut = new Date(timing.checkOutTime);

          attendanceTiming.checkInTime = checkIn.toTimeString().slice(0, 5);

          attendanceTiming.checkOutTime = checkOut.toTimeString().slice(0, 5);
        }
      } catch (err) {
        console.error(
          "❌ Failed to fetch attendance timing:",
          err.response?.data || err,
        );

        showToast(
          "error",
          err.response?.data?.error || "Failed to load attendance timing",
        );
      }

      // ✅ Open modal from query
      if (route.query.openPolicy === "true") {
        openPolicyModal();
        router.replace({ query: {} });
      }
    } catch (err) {
      console.error("❌ Failed to load dashboard:", err);

      showToast("error", "Failed to load school dashboard");
    } finally {
      isLoading.value = false;
    }
  });

  // 🏫 Get school from Vuex state
  const schoolData = computed(() => store.state.schools[0] || null);
  const userRole = computed(() => store.state.userRole);
  const leaveBalance = computed(() => store.state.leaveBalance);
  // 🧮 Calculate school age
  const getSchoolAge = () => {
    if (!schoolData.value?.establishmentYear) return "N/A";
    const currentYear = new Date().getFullYear();
    return currentYear - parseInt(schoolData.value.establishmentYear);
  };

  // 🌎 Convert country code to readable name
  const getCountryName = (code) => {
    const country = countries.value.find((c) => c.code === code);
    return country ? country.name : code;
  };

  // 🖱 Close dropdown menu if clicked outside
  const handleClickOutside = (event) => {
    if (!event.target.closest(".relative")) {
      showMenu.value = false;
    }
  };

  if (typeof window !== "undefined") {
    document.addEventListener("click", handleClickOutside);
  }

  const reloadPage = () => {
    window.location.reload();
  };
  const createWebsite = () => {
    // ONLY OPEN MODAL
    showWebsiteModal.value = true;
  };
  const confirmCreateWebsite = async () => {
    try {
      creatingWebsite.value = true;

      const res = await store.dispatch("createWebsite");

      showToast("success", `Website Created Successfully: ${res.websiteUrl}`);

      showWebsiteModal.value = false;
    } catch (err) {
      console.error(err);

      showToast(
        "error",
        err.response?.data?.message || "Failed to create website",
      );
    } finally {
      creatingWebsite.value = false;
    }
  };
</script>
<style scoped>
  /* ============================= */
  /* Modal Smooth Animation */
  /* ============================= */

  .modal-enter-active {
    transition: all 300ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  .modal-leave-active {
    transition: all 220ms ease;
  }

  .modal-enter-from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }

  .modal-enter-to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .modal-leave-from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .modal-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
</style>
